import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Home, Briefcase, MessageSquare } from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';
import { usePageMeta } from '../hooks/usePageMeta';

export const NotFound = () => {
  usePageMeta({
    title: 'Page Not Found | Tower Technology',
    description:
      'The page you are looking for could not be found on the Tower Technology website.',
    canonical: 'https://towertech-eng.com/',
  });

  React.useEffect(() => {
    // Explicitly tell crawlers not to index 404 responses
    let meta = document.querySelector('meta[name="robots"]');
    const previous = meta?.getAttribute('content');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'noindex, follow');
    return () => {
      // Restore on unmount so other routes are still indexable
      if (previous) meta.setAttribute('content', previous);
      else meta.setAttribute('content', 'index, follow');
    };
  }, []);

  return (
    <>
      <Header />
      <main className="pt-32 pb-24 bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50 min-h-screen">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-700 rounded-full mb-6">
                <AlertTriangle className="w-8 h-8 text-white" />
              </div>
              <p
                data-testid="not-found-code"
                className="text-sm font-bold tracking-wider text-blue-800 uppercase mb-3"
              >
                404 · Page Not Found
              </p>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                We couldn't find that page
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-800 mb-6"></div>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed max-w-2xl">
                The link you followed may be broken, or the page may have been
                moved. Please use one of the links below to continue.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <Link
                to="/"
                data-testid="not-found-home"
                className="group bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-md transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-700 rounded-lg mb-3">
                  <Home className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-base font-semibold text-gray-900 mb-1">
                  Home
                </h2>
                <p className="text-sm text-gray-600">
                  Return to the homepage
                </p>
              </Link>

              <Link
                to="/#services"
                data-testid="not-found-services"
                className="group bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-md transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-700 rounded-lg mb-3">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-base font-semibold text-gray-900 mb-1">
                  Our Services
                </h2>
                <p className="text-sm text-gray-600">
                  Engineering &amp; technical support
                </p>
              </Link>

              <Link
                to="/#contact"
                data-testid="not-found-contact"
                className="group bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-md transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-700 rounded-lg mb-3">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-base font-semibold text-gray-900 mb-1">
                  Discuss Your Project
                </h2>
                <p className="text-sm text-gray-600">
                  Get in touch with our team
                </p>
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};
